import React, { useState } from 'react';
import RRule from 'rrule';
import { QuickAppointmentContext } from './QuickAppointmentContext';
import { apiGet, apiPost } from '../brainApi';
import {
  endDate,
  handleRruleChange,
} from '../RecurringAppointments/RecurringPlanModal';
import { getZ3PConfigValue } from '../helper';

const QuickAppointmentProvider = ({ children }) => {
  const [toggle, setToggle] = useState(false);
  const [activeStep, setActiveStep] = useState(-1);
  const [customerType, setCustomerType] = useState('new');
  const [contactData, setContactData] = useState(null);
  const [addressData, setAddressData] = useState(null);
  const [vehicleData, setVehicleData] = useState(null);
  const [serviceData, setServiceData] = useState(null);
  const [couponCode, setCouponCode] = useState(null);
  const [customDiscountReason, setCustomDiscountReason] = useState(null);
  const [customDiscountAmount, setCustomDiscountAmount] = useState(null);
  const [existingData, setExistingData] = useState(null);
  const [scheduleData, setScheduleData] = useState(null);
  const [toggleSearch, setToggleSearch] = useState(null);
  const [calendarData, setCalendarData] = useState([]);
  const [savedData, setSavedData] = useState(null);
  const [note, setNote] = useState(null);
  const [paymentMethodId, setPaymentMethodId] = useState(null);
  const [paymentMethodList, setPaymentMethodList] = useState([]);
  const [loadingDates, setLoadingDates] = useState(false);
  const [conflictReason, setConflictReason] = useState(null);
  const [isUsingOptimalSchedule, setIsUsingOptimalSchedule] = useState(true);
  const [isLoadingAdditionalDates, setIsLoadingAdditionalDates] = useState(
    false,
  );
  const [isFirstAddressRender, setIsFirstAddressRender] = useState(true);
  const [availableDatesToShow, setAvailableDatesToShow] = useState(null);
  const [quoteId, setQuoteId] = useState(null);

  const { WEEKLY, MO, TU, WE, TH, FR, SA, SU } = RRule;
  const daysOfWeekMap = [
    { rruleValue: SU, text: 'Sunday' },
    { rruleValue: MO, text: 'Monday' },
    { rruleValue: TU, text: 'Tuesday' },
    { rruleValue: WE, text: 'Wednesday' },
    { rruleValue: TH, text: 'Thursday' },
    { rruleValue: FR, text: 'Friday' },
    { rruleValue: SA, text: 'Saturday' },
  ];

  const [isRecurring, setIsRecurring] = useState(false);
  const [rruleAttributes, setRruleAttributes] = useState({
    dtstart: new Date(),
    until: endDate(),
    freq: WEEKLY,
    byweekday: daysOfWeekMap[new Date().getDay()].rruleValue,
    interval: 1,
    byhour: 12,
  });
  const [rruleCombined, setRruleCombined] = useState(
    new RRule(rruleAttributes),
  );
  const [datesToCreate, setDatesToCreate] = useState(rruleCombined.all());
  const [isEndDateInvalid, setIsEndDateInvalid] = useState(false);
  const [unavailableRecurringDates, setUnavailableRecurringDates] = useState(
    [],
  );
  const [
    recurringDatesWithConflicts,
    setRecurringDatesWithConflicts,
  ] = useState([]);
  const [timeSlotOptions, setTimeSlotOptions] = useState([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
  // Keeps track of what steps have been "completed" by the user.
  // Ex: Once you successfully click continue on the vehicle form, that step will be marked as complete
  const [completedSteps, setCompletedSteps] = useState({});

  const isSkippingVehicle = getZ3PConfigValue('is_skipping_vehicle');

  const allSteps = isSkippingVehicle
    ? ['Contact', 'Address', 'Services', 'Schedule', 'Cost Preview']
    : ['Contact', 'Address', 'Vehicle', 'Services', 'Schedule', 'Cost Preview'];

  const handleNext = () => {
    setCompletedSteps((prevCompletedSteps) => ({
      ...prevCompletedSteps,
      [activeStep]: true,
    }));
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    //  If moving to the schedule screen, set recurring to false by default (recurring will be true if previous appointments are still in progress)
    if (allSteps[activeStep] === 'Services') setIsRecurring(false);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(-1);
    setCompletedSteps({});
    setCustomerType('new');
  };

  const handleOpen = () => {
    setToggle(true);
  };

  const handleClose = () => {
    setToggle(false);
    setContactData(null);
    setAddressData(null);
    setVehicleData(null);
    setServiceData(null);
    setCouponCode(null);
    setCustomDiscountReason(null);
    setCustomDiscountAmount(null);
    setScheduleData(null);
    setExistingData(null);
    setSavedData(null);
    setPaymentMethodId(null);
    setPaymentMethodList([]);
    setIsUsingOptimalSchedule(true);
    setIsFirstAddressRender(true);
    setQuoteId(null);
    handleReset();
  };

  const getSearchData = (customer_id) => {
    setToggleSearch(false);
    apiGet(`/customer/${customer_id}?no-sws=true`).then((data) => {
      setContactData(data);
      setExistingData(data);
    });
  };

  const onRruleChange = (attribute, value) => {
    const [endDateInvalid, rruleAttributeObj] = handleRruleChange(
      attribute,
      value,
      rruleAttributes,
    );
    setIsEndDateInvalid(endDateInvalid);
    setRruleAttributes(rruleAttributeObj);
    setRruleCombined(new RRule(rruleAttributeObj));
  };

  return (
    <QuickAppointmentContext.Provider
      value={{
        toggle,
        handleOpen,
        handleClose,
        activeStep,
        handleNext,
        handleBack,
        contactData,
        setContactData,
        addressData,
        setAddressData,
        vehicleData,
        setVehicleData,
        serviceData,
        setServiceData,
        couponCode,
        setCouponCode,
        customDiscountReason,
        setCustomDiscountReason,
        customDiscountAmount,
        setCustomDiscountAmount,
        scheduleData,
        setScheduleData,
        setCustomerType,
        customerType,
        existingData,
        setExistingData,
        setToggleSearch,
        toggleSearch,
        allSteps,
        setCompletedSteps,
        setActiveStep,
        savedData,
        setSavedData,
        note,
        setNote,
        setCalendarData,
        calendarData,
        getSearchData,
        paymentMethodId,
        setPaymentMethodId,
        paymentMethodList,
        setPaymentMethodList,
        completedSteps,
        loadingDates,
        setLoadingDates,
        isRecurring,
        setIsRecurring,
        onRruleChange,
        rruleAttributes,
        rruleCombined,
        isEndDateInvalid,
        datesToCreate,
        setDatesToCreate,
        unavailableRecurringDates,
        setUnavailableRecurringDates,
        recurringDatesWithConflicts,
        setRecurringDatesWithConflicts,
        conflictReason,
        setConflictReason,
        isUsingOptimalSchedule,
        setIsUsingOptimalSchedule,
        isLoadingAdditionalDates,
        setIsLoadingAdditionalDates,
        isFirstAddressRender,
        setIsFirstAddressRender,
        timeSlotOptions,
        setTimeSlotOptions,
        selectedTimeSlot,
        setSelectedTimeSlot,
        availableDatesToShow,
        setAvailableDatesToShow,
        quoteId,
        setQuoteId,
      }}
    >
      {children}
    </QuickAppointmentContext.Provider>
  );
};

export default QuickAppointmentProvider;
